import { Box, Button, Typography, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import {
  ChevronLeft as BackIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";
import { colorsFoundation, typographyTokens } from "../../../theme/index.js";

/**
 * JourneyWizard — the click-dummy's "Schaden melden" broker flow shell: a fixed
 * top horizontal stepper, a scrollable step body (pill badge + title + description
 * + fields), and a fixed footer nav (Abbrechen/Zurück · Entwurf speichern ·
 * Weiter/Einreichen).
 *
 * Drop-in alternative to StepperV2 — same MDDForm control contract (item.steps,
 * renderFields, errors, values, actionContext, progressMode). Adds:
 *  - dynamic/interjected steps: a step with a `when(values)` predicate is only
 *    included when it returns true (the "4a/4b" behaviour),
 *  - guided navigation: a `maxStep` high-water mark (can't jump ahead of it),
 *  - scroll-to-top of the body on step change,
 *  - consent gating: a step with `requireCheck` blocks advance until its named
 *    checkbox value is truthy.
 *
 * Accent reads from the primary key ramp, so it re-tints per brand skin.
 */
const ACCENT = colorsFoundation.key.blue[600];
const ACCENT_TINT = colorsFoundation.key.blue[50];
const ACCENT_BORDER = colorsFoundation.key.blue[200];
const HAIRLINE = colorsFoundation.gray[200];
const MUTED = colorsFoundation.gray[500];
const UPCOMING = colorsFoundation.gray[400];

export default function JourneyWizard(props) {
  const {
    item,
    errors = null,
    renderFields,
    progressMode = "lax",
    actionContext,
    values,
    isSubFormOpen = false,
  } = props;

  // Active steps = declared steps minus those whose `when(values)` is false.
  const steps = useMemo(
    () =>
      (item.steps || []).filter(
        (step) => typeof step.when !== "function" || step.when(values)
      ),
    [item.steps, values]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const bodyRef = useRef(null);

  // Clamp the active index if the active-step set shrank (a conditional step
  // disappeared) so we never point past the end.
  const lastIndex = Math.max(0, steps.length - 1);
  const safeActive = Math.min(activeStep, lastIndex);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [safeActive]);

  const current = steps[safeActive];

  const errorsForStep = (step) => {
    if (!step?.children) return 0;
    return step.children.filter((child) => !!errors?.[child.name]).length;
  };

  const isStepComplete = (index) => {
    const step = steps[index];
    if (!step) return false;
    const visited = progressMode === "full" || index < maxStep;
    return visited && errorsForStep(step) === 0;
  };

  const consentSatisfied = (step) =>
    !step?.requireCheck || Boolean(values?.[step.requireCheck]);

  const goTo = (index) => {
    if (index < 0 || index > lastIndex) return;
    if (index > maxStep) return; // guided: can't skip ahead of the high-water mark
    setActiveStep(index);
  };

  const handleBack = () => {
    if (safeActive === 0) {
      actionContext?.handleCancel?.();
      return;
    }
    setActiveStep(safeActive - 1);
  };

  const handleNext = () => {
    if (!consentSatisfied(current) || isSubFormOpen) return;
    const next = Math.min(lastIndex, safeActive + 1);
    setActiveStep(next);
    setMaxStep((prev) => Math.max(prev, next));
  };

  const isLast = safeActive === lastIndex;

  // Footer primary submit ("Schaden einreichen") on the last step delegates to
  // the form's report action (submitAction2), falling back to save.
  const { submitAction, submitAction2, handleOnClick, canSave, isValid } =
    actionContext || {};

  const reportActive =
    !isSubFormOpen &&
    (submitAction2?.activeWhen && typeof submitAction2.activeWhen === "function"
      ? submitAction2.activeWhen(isValid)
      : canSave);

  const draftDisabled =
    isSubFormOpen || !(submitAction?.alwaysActive || canSave);

  if (!current) return <Box />;

  const circle = (index) => {
    const complete = isStepComplete(index);
    const active = index === safeActive;
    if (complete) {
      return {
        backgroundColor: ACCENT,
        border: `2px solid ${ACCENT}`,
        color: colorsFoundation.neutral.white,
      };
    }
    if (active) {
      return {
        backgroundColor: ACCENT_TINT,
        border: `2px solid ${ACCENT}`,
        color: ACCENT,
        boxShadow: `0 0 0 4px ${colorsFoundation.key.blue[100]}`,
      };
    }
    return {
      backgroundColor: colorsFoundation.neutral.white,
      border: `2px solid ${HAIRLINE}`,
      color: UPCOMING,
    };
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "70vh",
        width: "100%",
      }}
    >
      {/* Top horizontal stepper */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          borderBottom: `1px solid ${colorsFoundation.gray[100]}`,
          pb: "16px",
          mb: "28px",
        }}
      >
        {steps.map((step, index) => {
          const complete = isStepComplete(index);
          const reachable = index <= maxStep;
          const st = circle(index);
          return (
            <Box
              key={step.key || step.label || index}
              onClick={() => reachable && !isSubFormOpen && goTo(index)}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: reachable && !isSubFormOpen ? "pointer" : "default",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                {/* left connector */}
                <Box
                  sx={{
                    flex: 1,
                    height: "2px",
                    backgroundColor:
                      index > 0 && isStepComplete(index - 1)
                        ? ACCENT
                        : index === 0
                          ? "transparent"
                          : HAIRLINE,
                  }}
                />
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                    fontSize: "13px",
                    fontWeight: 600,
                    ...st,
                  }}
                >
                  {complete ? (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  ) : (
                    index + 1
                  )}
                </Box>
                {/* right connector */}
                <Box
                  sx={{
                    flex: 1,
                    height: "2px",
                    backgroundColor: isStepComplete(index)
                      ? ACCENT
                      : index === steps.length - 1
                        ? "transparent"
                        : HAIRLINE,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  mt: "8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  textAlign: "center",
                  color:
                    complete || index === safeActive ? ACCENT : MUTED,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {step.railLabel || step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Scrollable body */}
      <Box ref={bodyRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ maxWidth: current.maxWidth || 680 }}>
          {/* Step pill badge */}
          <Box
            component="span"
            sx={{
              display: "inline-block",
              backgroundColor: ACCENT_TINT,
              border: `1px solid ${ACCENT_BORDER}`,
              borderRadius: "20px",
              padding: "4px 12px",
              fontSize: "12px",
              fontWeight: 600,
              color: ACCENT,
              mb: "12px",
            }}
          >
            {`Schritt ${safeActive + 1} von ${steps.length}`}
          </Box>

          <Typography
            sx={{
              fontSize: "21px",
              fontWeight: 700,
              letterSpacing: "-0.3px",
              color: colorsFoundation.gray[900],
              mb: current.description ? "6px" : "20px",
            }}
          >
            {current.label}
          </Typography>
          {current.description && (
            <Typography
              sx={{ fontSize: "14px", color: MUTED, mb: "20px" }}
            >
              {current.description}
            </Typography>
          )}

          <Grid container spacing={2}>
            {renderFields(current.children)}
          </Grid>
        </Box>
      </Box>

      {/* Fixed footer nav */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: `1px solid ${HAIRLINE}`,
          mt: "28px",
          pt: "16px",
        }}
      >
        <Button
          variant="tertiary"
          startIcon={<BackIcon />}
          disabled={isSubFormOpen}
          onClick={handleBack}
          sx={{ borderColor: HAIRLINE, color: MUTED }}
        >
          {safeActive === 0 ? "Abbrechen" : "Zurück"}
        </Button>

        <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {submitAction && (
            <Button
              variant="text"
              disabled={draftDisabled}
              onClick={(e) => handleOnClick?.(e, submitAction)}
              sx={{ color: MUTED }}
            >
              Entwurf speichern
            </Button>
          )}
          {isLast ? (
            <Button
              variant="contained"
              disabled={!reportActive}
              endIcon={<NextIcon />}
              onClick={(e) =>
                submitAction2 && handleOnClick?.(e, submitAction2)
              }
            >
              Schaden einreichen
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              disabled={isSubFormOpen || !consentSatisfied(current)}
              onClick={handleNext}
            >
              Weiter
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

JourneyWizard.propTypes = {
  item: PropTypes.object.isRequired,
  errors: PropTypes.object,
  renderFields: PropTypes.func.isRequired,
  progressMode: PropTypes.string,
  values: PropTypes.object,
  isSubFormOpen: PropTypes.bool,
  actionContext: PropTypes.object,
};
